/**
 * GeocodingService - Provides geocoding functionality using ESRI ArcGIS geocoding service
 *
 * This service converts place names to geographic coordinates and bounding boxes,
 * similar to how Voyager search handles the "place" parameter.
 */

import { logError, logDebug } from '../../../../utils/logger';
import type { LatLonPair } from '../models/search-result';

export interface GeocodeResult {
  name: string; // Display name (e.g., "USA", "California")
  id: string; // Place ID for Voyager (e.g., "__vg__7e5309cbf25a82a086a38095442c6398")
  finder: string; // Geocoder used (always "esri" for this service)
  bbox: number[]; // Bounding box [west, south, east, north]
  bboxCorners: LatLonPair[]; // Corners for map display
  center: {
    longitude: number;
    latitude: number;
  };
}

// ESRI geocoding API endpoint
const ESRI_GEOCODE_URL =
  'https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer/findAddressCandidates';

// Generate a unique place ID for Voyager compatibility
const generatePlaceId = (name: string): string => {
  // Create a hash-like ID similar to Voyager's format
  const hash = Array.from(name.toLowerCase()).reduce((acc, char) => {
    const code = char.charCodeAt(0);
    return ((acc << 5) - acc + code) | 0;
  }, 0);

  const hashHex = Math.abs(hash).toString(16).padStart(8, '0');
  const randomPart = Math.random().toString(16).slice(2, 26);
  return `__vg__${hashHex}${randomPart}`;
};

/**
 * Geocode a place name to get its bounding box and coordinates
 *
 * @param placeName - The place name to geocode (e.g., "USA", "California", "New York")
 * @returns GeocodeResult with place info and bbox, or null if not found
 */
export async function geocodePlace(
  placeName: string,
): Promise<GeocodeResult | null> {
  if (!placeName?.trim()) {
    return null;
  }

  try {
    const params = new URLSearchParams({
      SingleLine: placeName.trim(),
      f: 'json',
      outFields: 'Addr_type,PlaceName,Country,Region,Subregion',
      maxLocations: '1',
      forStorage: 'false',
    });

    const response = await fetch(`${ESRI_GEOCODE_URL}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`Geocoding request failed: ${response.status}`);
    }

    const data = await response.json();
    logDebug('Geocoding response:', data);

    if (!data.candidates || data.candidates.length === 0) {
      logDebug('No geocoding results found for:', placeName);
      return null;
    }

    const candidate = data.candidates[0];
    const { location, extent, attributes } = candidate;

    // Use the extent from the geocoding result
    // ESRI returns extent as { xmin, ymin, xmax, ymax }
    let bbox: number[];
    if (extent) {
      bbox = [extent.xmin, extent.ymin, extent.xmax, extent.ymax];
    } else {
      // Fallback: create a small bbox around the point
      const buffer = 0.5; // degrees
      bbox = [
        location.x - buffer,
        location.y - buffer,
        location.x + buffer,
        location.y + buffer,
      ];
    }

    // Convert bbox to corner points for map display
    // Format: [SW, SE, NE, NW] with { longitude, latitude }
    const bboxCorners: LatLonPair[] = [
      { longitude: bbox[0], latitude: bbox[1] }, // SW
      { longitude: bbox[2], latitude: bbox[1] }, // SE
      { longitude: bbox[2], latitude: bbox[3] }, // NE
      { longitude: bbox[0], latitude: bbox[3] }, // NW
    ];

    // Build the display name
    const displayName = attributes?.PlaceName || placeName;

    const result: GeocodeResult = {
      name: displayName,
      id: generatePlaceId(displayName),
      finder: 'esri',
      bbox,
      bboxCorners,
      center: {
        longitude: location.x,
        latitude: location.y,
      },
    };

    logDebug('Geocoding result:', result);
    return result;
  } catch (error) {
    logError('Geocoding error:', error);
    return null;
  }
}

/**
 * Lookup a place by its ID (for restoring from URL)
 * Note: This is a simplified implementation - in a full implementation,
 * you might cache geocoding results or use a different lookup mechanism.
 *
 * @param placeId - The place ID (e.g., "__vg__...")
 * @param placeName - The place name to re-geocode
 * @returns GeocodeResult or null
 */
export async function lookupPlaceById(
  placeId: string,
  placeName: string,
): Promise<GeocodeResult | null> {
  // For now, just re-geocode the place name
  // A more sophisticated implementation might cache results
  const result = await geocodePlace(placeName);

  if (result) {
    // Preserve the original place ID
    result.id = placeId;
  }

  return result;
}

const GeocodingService = {
  geocodePlace,
  lookupPlaceById,
};
export default GeocodingService;
